import { PlanType } from '@labrav/react-components'
import { KeyValue } from '../../../modules/privateOffer/privateOfferCreation/types'
import { Dimension, EulaType } from '../../../modules/privateOffer/types'
import { ResaleAuthorizationWithErrors } from '../../../modules/resaleAuthorization/types'
import { Eula } from '../../PrivateOfferForm/PrivateOfferCreation/Eula'
import { AuthorizationDuration } from './AuthoizationDuration/AuthorizationDuration'
import { Buyer } from './Buyer/Buyer'
import { DiscountDetails } from './DiscountDetails/DiscountDetails'
import { ResaleAuthorizationDetails } from './ResaleAuthorizationDetails/ResaleAuthorizationDetails'
import { eulaStandardContractUrl } from '../../../common/utils/constants'
import { standardResellerAgreementUrl } from '../constants'
import { getEulaOptionsForResaleAuthorization } from '../helpers'
import { MetaDataComponent } from '../../PrivateOfferForm/PrivateOfferCreation/MetaDataComponent'
import { PricingComponent } from '../../PrivateOfferForm/PricingComponent/PricingComponent'
import { PrivateOfferDimensions } from '../../PrivateOfferForm/PrivateOfferDimensions/PrivateOfferDimensions'
import { Renewal } from '../../PrivateOfferForm/Renewal/Renewal'
import {
  Product,
  resalePricesKey,
} from '../../../modules/productsListing/types'
import { get, isEmpty } from 'lodash'
import {
  AgreementDurationType,
  ContractDurationType,
  DiscountType,
} from '../../../admin/modules/resaleAuthorizations'
import { Reseller } from '../Reseller/Reseller'

type ResaleAuthorizationCreationProps = {
  resaleAuthorization: ResaleAuthorizationWithErrors
  updateValuesInArray: (
    index: number,
    key: string,
    type?: string
  ) => (data: KeyValue) => void
  addRow: (key: string, type?: string) => void
  deleteRow: (index: number, key: string) => () => void
  handleChange: (data: KeyValue) => void
  selectedEulaFiles: File[]
  setEulaFile: (files: File[]) => void
  selectedResellerAgreementFiles: File[]
  setResellerAgreementFile: (files: File[]) => void
  product: Product
  updateDimension: (index: number, dimension: Dimension) => void
  updatePricesOnDimensions: (index: number, priceData: KeyValue) => void
}
export const ResaleAuthorizationCreation = ({
  resaleAuthorization,
  updateValuesInArray,
  addRow,
  deleteRow,
  handleChange,
  selectedEulaFiles,
  setEulaFile,
  selectedResellerAgreementFiles,
  setResellerAgreementFile,
  product,
  updateDimension,
  updatePricesOnDimensions,
}: ResaleAuthorizationCreationProps) => {
  const handleUpdateDimensionPrices = (index: number) => (data: KeyValue) => {
    updatePricesOnDimensions(index, data)
  }
  const isCustom =
    resaleAuthorization?.discountType === DiscountType.INDIVIDUAL_PRICING &&
    resaleAuthorization.contractDurationType === ContractDurationType.CUSTOM
  const handleUpdateDimensionRow =
    (index: number, key: string, type?: string) => (data: KeyValue) => {
      const isUpdateInPrices =
        resalePricesKey.includes(data.key) ||
        product?.productType === PlanType.SAAS_SUBSCRIPTION

      return isUpdateInPrices
        ? handleUpdateDimensionPrices(index)(data)
        : updateValuesInArray(index, key, type)(data)
    }
  return (
    <>
      <ResaleAuthorizationDetails
        resaleAuthorizationWithError={resaleAuthorization}
        addOfferRecipient={addRow}
        deleteOfferRecipient={deleteRow}
        updateRecipientsData={updateValuesInArray}
        handleDetailsChange={handleChange}
      />
      <Renewal
        renewal={resaleAuthorization?.renewal || false}
        channel={resaleAuthorization?.renewalChannel || null}
        handleDetailsChange={handleChange}
        offerType={'resale authorization'}
      />
      <Reseller
        resellersInfo={{ ...get(resaleAuthorization, 'resellersInfo[0]', {}) }}
        handleUpdatedArray={updateValuesInArray}
        handleChange={handleChange}
        error={{
          labraPlatformResellerId: (resaleAuthorization?.errors
            ?.labraPlatformResellerId || '') as string,
          resellerAwsAccountId: (resaleAuthorization?.errors
            ?.resellerAwsAccountId || '') as string,
        }}
      />
      <DiscountDetails
        resaleAuthorizationWithError={resaleAuthorization}
        handleChange={handleChange}
        planType={product?.productType || PlanType.SAAS_CONTRACT}
      />
      {!isEmpty(resaleAuthorization?.discountType) &&
        resaleAuthorization?.discountType !== DiscountType.DISCOUNT && (
          <PrivateOfferDimensions
            dimensions={get(resaleAuthorization, 'dimensions', []) || []}
            flexiblePaymentTerms={
              resaleAuthorization.discountType ===
              DiscountType.FLEXIBLE_PAYMENT_TERMS
            }
            product={product}
            handleUpdateDimensionRow={handleUpdateDimensionRow}
            deleteDimensionsRow={deleteRow}
            addDimension={addRow}
            handleChange={handleChange}
            updateStandardDimensions={updateDimension}
            isResale={true}
            isCustom={isCustom}
            pricesArray={
              isCustom ? ['price'] : resaleAuthorization?.pricesArray
            }
            dimensionsError={{
              ...((resaleAuthorization.errors?.dimensions as Record<
                string,
                unknown
              >[]) || []),
            }}
          />
        )}
      {product?.productType === PlanType.SAAS_CONTRACT &&
        resaleAuthorization.discountType ===
          DiscountType.FLEXIBLE_PAYMENT_TERMS && (
          <PricingComponent
            updateInstallments={updateValuesInArray}
            deleteInstallmentsRow={deleteRow}
            handlePricingCheckbox={handleChange}
            flexiblePaymentTerms={true}
            installments={resaleAuthorization.installmentInfo || []}
            addInstallmentRows={addRow}
            planType={product?.productType || PlanType.SAAS_CONTRACT}
            errors={[
              ...(resaleAuthorization.errors.installmentInfo
                ? (resaleAuthorization.errors.installmentInfo as Array<
                    Record<string, unknown>
                  >)
                : []),
            ]}
            isResale={true}
            heading={'Payment Schedule'}
            subHeading={'Additional pricing configuration'}
          />
        )}
      <AuthorizationDuration
        handleChange={handleChange}
        agreementDurationType={
          resaleAuthorization.agreementDurationType ||
          AgreementDurationType.NO_SET_TIME
        }
        agreementExpirationDate={
          resaleAuthorization.agreementExpirationDate || ''
        }
        error={
          (resaleAuthorization?.errors?.agreementExpirationDate as string) || ''
        }
        discountType={resaleAuthorization.discountType || DiscountType.DISCOUNT}
      />
      <Buyer
        buyerAwsAccountId={resaleAuthorization.buyerAwsAccountId || ''}
        handleChange={handleChange}
        error={(resaleAuthorization?.errors?.buyerAwsAccountId as string) || ''}
      />

      <Eula
        selectedEulaFiles={selectedEulaFiles}
        setEulaFile={setEulaFile}
        handleChange={handleChange}
        eulaType={resaleAuthorization.endCustomerEulaType || EulaType.STANDARD}
        fileUploadHeading={'End user License agreement Documents'}
        fileUploadSubHeading={
          'Upload your custom End user License agreement(s) for this specific Resale Authorization'
        }
        heading={'End user license agreement for the buyer'}
        subHeading={'End user License agreement'}
        tooltipLabel={
          'Select end user license agreement or add your own custom end user license agreement files.'
        }
        deleteLink={deleteRow}
        disableUpload={
          (resaleAuthorization.endCustomerEulaFiles || []).length +
            selectedEulaFiles.length >=
          5
        }
        eulaTypeKey={'endCustomerEulaType'}
        eulaFileKey={'endCustomerEulaFiles'}
        eulaOptionTitle={'Select or upload a end user license agreement'}
        eulaOptions={getEulaOptionsForResaleAuthorization({
          standardUrl: eulaStandardContractUrl,
          eulaKey: 'endCustomerEulaType',
          productEulaUrl: product?.eulaUrl || '',
        })}
        showFileUploadForOptions={[]}
        fileLinks={resaleAuthorization?.endCustomerEulaFiles || []}
        showRealName
        checkDuplicate
      />
      <Eula
        selectedEulaFiles={selectedResellerAgreementFiles}
        setEulaFile={setResellerAgreementFile}
        handleChange={handleChange}
        eulaType={
          resaleAuthorization.resellerLicenseAgreementType || EulaType.STANDARD
        }
        fileUploadHeading={'Reseller agreement Documents'}
        fileUploadSubHeading={
          'Upload your custom Reseller agreement(s) for this specific Resale Authorization'
        }
        heading={'Reseller agreement - optional'}
        subHeading={'Reseller agreement'}
        tooltipLabel={
          'Select reseller agreement or add your own custom reseller agreement files.'
        }
        deleteLink={deleteRow}
        disableUpload={
          (resaleAuthorization.resellerLicenseAgreementFiles || []).length +
            selectedResellerAgreementFiles.length >=
          5
        }
        eulaTypeKey={'resellerLicenseAgreementType'}
        eulaFileKey={'resellerLicenseAgreementFiles'}
        eulaOptionTitle={'Select or upload a reseller agreement'}
        eulaOptions={getEulaOptionsForResaleAuthorization({
          standardUrl: standardResellerAgreementUrl,
          eulaKey: 'resellerLicenseAgreementType',
        })}
        showFileUploadForOptions={[]}
        fileLinks={resaleAuthorization?.resellerLicenseAgreementFiles || []}
        showRealName
        checkDuplicate
      />
      <MetaDataComponent
        heading="Custom metadata"
        tooltipLabel="Attaching any custom key-values to the Resale Authorization"
        metaData={resaleAuthorization.metaData || []}
        updateMetaData={updateValuesInArray}
        deleteMetaDataRow={deleteRow}
        addMetaDataRows={addRow}
        isResaleAuthorization={true}
        errors={[
          ...(resaleAuthorization?.errors?.metaData
            ? (resaleAuthorization?.errors?.metaData as Array<
                Record<string, unknown>
              >)
            : []),
        ]}
      />
    </>
  )
}

export { getEulaOptionsForResaleAuthorization } from '../helpers'
